import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import Product from 'src/app/model/product/product';
import ProductHelper, {
  PRODUCT_FACTORIES
} from 'src/app/helpers/product-helper';
import { ProductTypes } from 'src/app/model/product/product-type';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SnackService } from 'src/app/services/snack.service';
import { Project } from 'src/app/model/project/project';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { CLIENT_WINDOW_STICKER_TYPE } from 'src/app/model/product/windowsticker/client-windowsticker';

@Component({
  selector: 'client-product-creator',
  templateUrl: './client-product-creator.component.html',
  styleUrls: ['./client-product-creator.component.scss']
})
export class ClientProductCreatorComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit {
  @Input()
  inputProduct: Product;
  @Input()
  inputProject: Project;
  @Input()
  oldSystemImmo: boolean = false;

  @Output()
  productInitialized = new EventEmitter<Product>();

  PRODUCT_TYPES = ProductTypes;
  CLIENT_WINDOW_STICKER_TYPE = CLIENT_WINDOW_STICKER_TYPE;

  projectHasIPBoard: boolean = false;
  projectHasNonIPBoard: boolean = false;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit(): void {
    this.evaluateAddedProducts();

    const evaluateAddedProductsObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservableService.getProductAddedSubscribable(),
      this.projectDisplayObservableService.getSignSelectedSubscribable()
    );
    this.addSubscription(evaluateAddedProductsObservable, () =>
      this.evaluateAddedProducts()
    );
  }

  initializeIPBoard() {
    if (this.projectHasNonIPBoard) {
      this.snackService.showError(
        'U kan IP-borden niet combineren met andere soorten producten'
      );
      return;
    }
    this.productInitialized.emit(
      PRODUCT_FACTORIES.IP_BOARD_FACTORY.createProduct(null)
    );
  }

  initializeWindowSticker() {
    if (this.projectHasIPBoard) {
      this.snackService.showError(
        'U kan IP-borden niet combineren met andere soorten producten'
      );
      return;
    }
    this.productInitialized.emit(
      PRODUCT_FACTORIES.WINDOWSTICKER_FACTORY.createProduct(
        PRODUCT_FACTORIES.WINDOWSTICKER_FACTORY.getProductCreationParameters({
          type: CLIENT_WINDOW_STICKER_TYPE
        })
      )
    );
  }

  initializeBoard() {
    if (this.projectHasIPBoard) {
      this.snackService.showError(
        'U kan IP-borden niet combineren met andere soorten producten'
      );
      return;
    }
    this.productInitialized.emit(
      PRODUCT_FACTORIES.BOARD_FACTORY.createProduct(null)
    );
  }

  initializeOtherProduct() {
    if (this.projectHasIPBoard) {
      this.snackService.showError(
        'U kan IP-borden niet combineren met andere soorten producten'
      );
      return;
    }
    this.productInitialized.emit(
      PRODUCT_FACTORIES.OTHER_PRODUCT_FACTORY.createProduct(null)
    );
  }

  private evaluateAddedProducts(): void {
    if (this.oldSystemImmo) {
      this.projectHasIPBoard = true;
      this.projectHasNonIPBoard = false;
      return;
    }
    if (!CollectionHelper.isPopulated(this.inputProject.products)) {
      this.projectHasIPBoard = false;
      this.projectHasNonIPBoard = false;
      return;
    }

    let hasIPBoard = false;
    let hasNonIPBoard = false;

    for (const product of this.inputProject.products) {
      hasIPBoard =
        hasIPBoard ||
        (ProductHelper.isIPBoard(product) &&
          this.ipBoardRestrictsProductTypeSelection(<IPBoard>product));
      hasNonIPBoard =
        hasNonIPBoard ||
        ProductHelper.isWindowSticker(product) ||
        ProductHelper.isCustomWindowSticker(product) ||
        product.type === CLIENT_WINDOW_STICKER_TYPE ||
        ProductHelper.isBoard(product) ||
        ProductHelper.isOtherProduct(product);

      if (hasIPBoard && hasNonIPBoard) {
        break;
      }
    }

    this.projectHasIPBoard = hasIPBoard;
    this.projectHasNonIPBoard = hasNonIPBoard;
  }

  private ipBoardRestrictsProductTypeSelection(ipBoard: IPBoard): boolean {
    return (
      ipBoard.id != null ||
      this.ipBoardPartRestrictsProductTypeSelection(ipBoard.boardPart) ||
      this.ipBoardPartRestrictsProductTypeSelection(ipBoard.otherSideBoardPart)
    );
  }

  private ipBoardPartRestrictsProductTypeSelection(
    ipBoardPart: IPBoardPart
  ): boolean {
    return ipBoardPart != null && ipBoardPart.signOptionSelected;
  }
}
